import React, { useEffect, useState } from 'react';
import { Layout as AntdLayout, message, Spin, Result, Button } from 'antd';
import { useLocation, useHistory } from 'react-router-dom';
import * as qs from 'query-string';
import Api from 'api';
import { markSignedIn } from '../../helper';
import config from 'config';

const CspLaunchHandler: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleLaunch = async () => {
      const queries = qs.parse(location.search);
      const code = queries.code as string;
      // The launch code is single-use; take it out of the address bar right away
      // so it is not left in history, whatever happens next.
      if (code) window.history.replaceState(null, '', location.pathname);

      if (!code) {
        setError('No launch code provided. Please try signing in again from California Species Project.');
        setLoading(false);
        return;
      }

      try {
        const { data } = await Api.post('/auth/csp-launch', { code });

        if (data.token) {
          // Successful login — the API set the httpOnly session cookie.
          markSignedIn();

          // Check if this launch includes a species list payload from CSP
          if (data.payload && data.payload.action === 'import_species_list') {
            try {
              // Import the species list into a new SLP collection
              const importRes = await Api.post('/import/csp-list', {
                species: data.payload.species || [],
                project_name: data.payload.project?.name || 'Imported from CA Species',
                location_name: data.payload.project?.location_name || null,
                project_type: data.payload.project?.type || null,
              });
              if (importRes.data?.success && importRes.data?.collection_id) {
                message.success(
                  `Imported ${importRes.data.matched_count}/${importRes.data.total_count} species into "${importRes.data.collection_name}"`
                );
                history.replace(`/collection/show/${importRes.data.collection_id}`);
                return;
              }
            } catch (importErr: any) {
              console.error('CSP import failed:', importErr);
              message.warning('Species import had issues. Redirecting to dashboard.');
            }
          }

          // Normal login redirect
          history.replace('/dashboard');
        } else if (data.requires_verification) {
          // IP verification required - redirect to verify identity
          history.replace(`/verifyIdentity?token=${data.identity_token}`);
        } else {
          setError('Unexpected response from server. Please try again.');
          setLoading(false);
        }
      } catch (e: any) {
        const errorMsg =
          (e?.response?.data?.messages?.[0]) ||
          'Failed to process login. Please try again from California Species Project.';
        setError(errorMsg);
        setLoading(false);
      }
    };

    handleLaunch();
  }, [location.search, history]);

  if (loading && !error) {
    return (
      <AntdLayout style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" tip="Signing you in..." />
      </AntdLayout>
    );
  }

  if (error) {
    return (
      <AntdLayout style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Result
          status="error"
          title="Login Failed"
          subTitle={error}
          extra={[
            <Button type="primary" key="csp" href={config.csp_login_url}>
              Return to California Species Project
            </Button>,
            <Button key="signup" href={config.csp_signup_url}>
              Create an account
            </Button>,
            <Button key="login" onClick={() => history.replace('/login')}>
              Back to Login
            </Button>,
          ]}
        />
      </AntdLayout>
    );
  }

  return null;
};

export default CspLaunchHandler;
